<script setup lang="ts">
/**
 * The chat's composer (MAR-948, out of `ChatPanel.vue`, which used to spell it out twice): the
 * attached documents, a failed upload's message, and the input. `placement` is where it sits — under
 * the thread, or centred on the welcome screen.
 *
 * Two lines in both placements (MAR-1057, approved by Alex 2026-10-06): `ChatInput`'s `box` card
 * holds "+", the input, the wand, dictation and send; the line under it holds the scope pill, the
 * one-line disclaimer, the jurisdiction picker (multi-jurisdiction orgs only, MAR-1418) and the model.
 * The scope picker and the route's scope offer render above the
 * card. The Word add-in keeps its own `compact` composer.
 */
import type { ChatImage } from '~/types/web-chat'
import ChatContextBar from '~/components/chat/ChatContextBar.vue'
import ChatScopeBar from '~/components/chat/ChatScopeBar.vue'
import ChatJurisdictionPicker from '~/components/chat/ChatJurisdictionPicker.vue'
import { useChatPlaceholders } from '~/composables/web-chat/useChatPlaceholders'

const props = defineProps<{
  placement: 'thread' | 'welcome'
  /** The panel floats over a page — narrower paddings, the compact input everywhere. */
  compact: boolean
  /** A disallowed file's name(s) from the most recent drop or pick (#804). */
  uploadError: string
}>()

const emit = defineEmits<{
  send: [content: string, images?: ChatImage[]]
  'pick-document': []
  'upload-files': [files: File[]]
}>()

const text = defineModel<string>({ required: true })

const { t } = useI18n()
const route = useRoute()
const webChat = useWebChatStore()
const { placeholder, placeholders } = useChatPlaceholders(computed(() => route.path), t)

const busy = computed(() => webChat.isStreaming || !!webChat.toolStatus)
// A pinned document still uploading or processing blocks the send: its content isn't ready for the
// agent. (An errored one does not — its chip shows red and can be removed.)
const sendBlockedHint = computed(() => {
  const n = webChat.attachmentsProcessingCount
  return n <= 1 ? t('webChat.waitForProcessing') : t('webChat.waitForProcessingPlural', { n })
})
const thread = computed(() => props.placement === 'thread')
</script>

<template>
  <!-- Under a thread the composer floats in the conversation (Alex, 2026-10-07, after Claude's): no
       band or rule of its own, the card as wide as the transcript's `max-w-3xl` column, and the
       transcript fading out just above it. -->
  <div :class="thread ? 'relative shrink-0 px-3 pb-3' : ['w-full space-y-2', props.compact ? 'max-w-full px-2' : 'max-w-[900px]']">
    <div v-if="thread" aria-hidden="true" class="pointer-events-none absolute inset-x-0 bottom-full h-6 bg-gradient-to-t from-background to-transparent" />
    <div data-ph-mask :class="thread ? 'mx-auto w-full max-w-3xl space-y-2' : 'space-y-2'">
      <ChatContextBar />
      <p v-if="props.uploadError" class="text-xs text-destructive">{{ props.uploadError }}</p>
      <ChatInput
        v-model="text"
        variant="box"
        :placeholder="placeholder"
        :placeholders="placeholders"
        :busy="busy"
        :send-blocked="webChat.attachmentsProcessing"
        :send-blocked-hint="sendBlockedHint"
        document-actions
        @send="(content, images) => emit('send', content, images)"
        @cancel="webChat.cancel()"
        @drop-item="webChat.attachDocument($event)"
        @pick-document="emit('pick-document')"
        @upload-files="emit('upload-files', $event)"
      >
        <!-- Scope (§6b): the picker unfolds above the card; the pill sits on the line under it. -->
        <template #above><ChatScopeBar part="panel" :compact="thread || props.compact" /></template>
        <template #footer-left><ChatScopeBar part="trigger" :compact="thread || props.compact" /></template>
        <template #footer-center><ChatDisclaimer short /></template>
        <!-- MAR-1418: one jurisdiction per conversation; renders nothing for a single-jurisdiction org. -->
        <template #footer-right><ChatJurisdictionPicker /></template>
      </ChatInput>
    </div>
  </div>
</template>
